import * as purify from 'dompurify';
import { Marked } from 'marked';

// The project has no allowSyntheticDefaultImports; webpack loads dompurify's
// ES build, whose DOMPurify instance is the default export.
const DOMPurify = ((purify as any).default || purify) as typeof purify;

/**
 * Markdown for Duesy answers: no raw HTML (escaped, not rendered), only
 * http(s)/mailto links, and external links open in a new tab with
 * rel="noopener noreferrer". DOMPurify runs last as the safety net.
 */

const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c] as string));

const marked = new Marked({
  gfm: true,
  breaks: true,
  async: false,
  renderer: {
    // Raw HTML in a model answer is shown as text, never rendered.
    html: (html: string) => escapeHtml(html),
  },
});

const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'ul', 'ol', 'li', 'a', 'blockquote', 'h3', 'h4', 'h5', 'h6', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td'];
const SAFE_URL = /^(?:https?:|mailto:)/i;

let hooksInstalled = false;
function installHooks(): void {
  if (hooksInstalled) { return; }
  hooksInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName !== 'A') { return; }
    const href = node.getAttribute('href') || '';
    if (!SAFE_URL.test(href)) {
      node.removeAttribute('href');
      return;
    }
    if (/^https?:/i.test(href)) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

/** Renders model markdown to sanitized HTML. */
export function renderDuesyMarkdown(markdown: string): string {
  installHooks();
  const html = marked.parse(markdown || '') as string;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['href', 'target', 'rel', 'title'],
    ALLOWED_URI_REGEXP: SAFE_URL,
  });
}
